import { forwardRef } from 'react'
import { cn } from './cn'
import { Spinner } from './spinner'

export type IconButtonVariant =
	| 'primary'
	| 'accent'
	| 'ghost'
	| 'danger'
	| 'on-dark'
	| 'info'
	| 'warning'
export type IconButtonSize = 'xs' | 'sm' | 'md' | 'lg'

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: IconButtonVariant
	size?: IconButtonSize
	loading?: boolean
	/** Toggle state — when true, renders in the variant style even if ghost. */
	pressed?: boolean
	/** Accessible label — REQUIRED for icon-only buttons */
	'aria-label': string
}

const base =
	'inline-flex items-center justify-center transition-all duration-200 ease-out disabled:opacity-50 disabled:cursor-not-allowed shrink-0'

const sizeMap: Record<IconButtonSize, string> = {
	xs: 'w-6 h-6 rounded-md [&>svg]:w-3.5 [&>svg]:h-3.5',
	sm: 'w-8 h-8 rounded-lg [&>svg]:w-4 [&>svg]:h-4',
	md: 'w-10 h-10 rounded-xl [&>svg]:w-[18px] [&>svg]:h-[18px]',
	lg: 'w-12 h-12 rounded-xl [&>svg]:w-5 [&>svg]:h-5',
}

const variantMap: Record<IconButtonVariant, string> = {
	primary: 'bg-primary text-primary-content hover:brightness-95 shadow-soft',
	accent: 'bg-accent text-accent-content hover:brightness-95 shadow-soft',
	ghost: 'text-base-content/70 hover:bg-base-200 hover:text-base-content active:bg-base-300',
	danger: 'text-coral-600 hover:bg-coral-500/10',
	'on-dark': 'text-white/70 hover:text-white hover:bg-white/10',
	info: 'bg-sage-400/15 text-sage-500 hover:bg-sage-400/25',
	warning: 'bg-amber-400/15 text-amber-500 hover:bg-amber-400/25',
}

const pressedMap: Record<IconButtonVariant, string> = {
	primary: 'bg-primary text-primary-content',
	accent: 'bg-accent text-accent-content',
	ghost: 'bg-base-200 text-base-content',
	danger: 'bg-coral-500 text-white',
	'on-dark': 'bg-white/20 text-white',
	info: 'bg-sage-500 text-white',
	warning: 'bg-amber-500 text-white',
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
	{
		variant = 'ghost',
		size = 'md',
		loading,
		pressed,
		className,
		children,
		disabled,
		type = 'button',
		...rest
	},
	ref,
) {
	return (
		<button
			ref={ref}
			type={type}
			disabled={disabled || loading}
			aria-pressed={pressed}
			aria-busy={loading || undefined}
			className={cn(
				base,
				sizeMap[size],
				pressed ? pressedMap[variant] : variantMap[variant],
				className,
			)}
			{...rest}
		>
			{loading ? <Spinner size="sm" /> : children}
		</button>
	)
})
